// SelectAccountFlow — the account half of the switch layer (contract 07 §8, ORP-18636). v3's
// `flows/SelectAccountFlow.ts` (25 ln) is a flag-dispatch shim and NOTHING of it survives; the real
// behaviour lives in `BootstrapV2Flow.executeAccountSwitch` (`:90-126`) and is what this ports:
// guard `account?.id` → `setAccount` + cache the selection → account-level bootstrap
// (`bootAccountOnly`) → hydrate account level → reset + re-pick workspace → workspace-level
// bootstrap → `finalize()`. `user`/`accounts` are KEPT across the switch (no `resetState` — the
// account-level sections simply re-hydrate them with fresh data; contract §7).
//
// Like its workspace sibling it NEVER auto-logs-out (v3 `:121`), NEVER writes `session.status` (a
// switch is not a re-boot — `sessionStore` stays the single writer) and classifies failures by
// `ApiError` rather than English prose (ledger A2).
//
// The workspace half is DELEGATED to `SelectWorkspaceFlow.switchTo` — v3's account switch ends in
// exactly a workspace switch (`:104-114`), so the reset/boot/hydrate/finalize sequence lives once.
//
// `hydrateAccountLevel`/`pickWorkspace`/`userKeyOf` are the shared `flowToolkit` (internal to
// `flows/`), which all three flows now consume — one copy, not three.
//
// ORP-21835 item 3 adds ONE thing on top of that port: a successful switch also answers WHERE it
// should land. The flow stays headless — it returns a target and never touches a router; the
// component navigates. See `resolveLanding` below for why the answer only exists here.
import type { RouteLocationRaw } from 'vue-router'

import type { AccountItem } from '../models/account.types'
import { BootstrapService } from '../service/BootstrapService'
import { workspacePreferencesService } from '../service/WorkspacePreferencesService'
import { useAccountStore, type AccountStore } from '../stores/accountStore'
import { useWorkspaceStore, type WorkspaceStore } from '../stores/workspaceStore'

import { FlowToolkit, toApiError, userKeyOf } from './flowToolkit'
import { dispatchBootstrap } from './hydratorRegistry'
import { SelectWorkspaceFlow } from './SelectWorkspaceFlow'

import type { ApiError } from '@/shared/http'

/** The MCP-only shell's landing route. The literal mirrors `app/router/routeNames`'s
 *  `MCP_HOME_ROUTE_NAME`; it cannot be imported, because a feature may not reach into `app/` (the
 *  one-way layer rule). `AuthCheckPermissionFlow` repeats it for the invite funnel for the same
 *  reason, and those two are the whole set. */
const MCP_LANDING: RouteLocationRaw = { name: 'McpAdAccounts' }

/** The outcome of an account select. Returned rather than toasted — the flow stays headless and the
 *  copy stays in the component (contract §13). `failed` carries the CLASSIFIED `ApiError`.
 *
 *  The three statuses are unchanged (ORP-18636). `switched` may additionally carry a `landing` —
 *  the route the caller should replace to — and OMITS the key entirely whenever the switch should
 *  leave the user where they are, which is every web account (see `resolveLanding`). */
export type AccountSwitchOutcome =
  | { status: 'unchanged' }
  | { status: 'switched'; landing?: RouteLocationRaw }
  | { status: 'failed'; error: ApiError }

export interface SelectAccountFlowOptions {
  bootstrapService?: BootstrapService
  accountStore?: AccountStore
  workspaceStore?: WorkspaceStore
  dispatch?: typeof dispatchBootstrap
  /** The workspace half (injected for tests; defaults to a real flow sharing the same stores). */
  workspaceFlow?: SelectWorkspaceFlow
}

export class SelectAccountFlow {
  private readonly bootstrapService: BootstrapService
  private readonly accountStore: AccountStore
  private readonly workspaceFlow: SelectWorkspaceFlow
  /** The hydrate + pick-workspace toolkit shared with the boot + workspace-switch flows. No hooks:
   *  this flow raises no package signal of its own — its `finalize` is the workspace half's. */
  private readonly toolkit: FlowToolkit

  constructor(options: SelectAccountFlowOptions = {}) {
    this.bootstrapService = options.bootstrapService ?? new BootstrapService()
    this.accountStore = options.accountStore ?? useAccountStore()
    this.workspaceFlow = options.workspaceFlow ?? new SelectWorkspaceFlow()
    this.toolkit = new FlowToolkit({
      accountStore: this.accountStore,
      workspaceStore: options.workspaceStore ?? useWorkspaceStore(),
      dispatch: options.dispatch ?? dispatchBootstrap,
    })
  }

  /**
   * The account-select use case. `session.status` never changes — a switch is not a re-boot.
   *   • no id / already the current account → no-op, ZERO network.
   *   • otherwise → set + cache the selection → `bootAccountOnly(refresh)` → hydrate account level
   *     → re-pick a workspace from the NEW account's list → workspace switch (reset + boot +
   *     hydrate + finalize).
   */
  public async execute(account: AccountItem | null): Promise<AccountSwitchOutcome> {
    if (!account?.id) return { status: 'unchanged' } // v3 `SelectAccountFlow.ts:13` — a no-op.
    // FIX (analysis R9 / ledger §C): v3's switcher guarded on `accountStore.selectedAccountid`
    // (`AccountSelect.vue:70`) — a getter that DOES NOT EXIST (the real one is `accountId`), so the
    // guard was permanently `undefined`, never matched, and re-selecting the account you are
    // already on ran a full account-switch bootstrap. Guarding in the flow (not the component)
    // fixes it for every caller — the switcher, `?account_id` and auth's handoff delegation alike.
    if (Number(this.accountStore.accountId) === Number(account.id)) return { status: 'unchanged' }

    // Commit + cache BEFORE the fetch, as v3 did (`:91-93`, ahead of its try at `:97`): a failed
    // account boot therefore leaves the new account selected with the previous account's detail,
    // and the caller surfaces the failure. Parity is deliberate — moving the commit after the fetch
    // would change which account the cached selection points at on the next warm boot.
    this.accountStore.setAccount(account)
    workspacePreferencesService.clear()
    const userKey = userKeyOf(this.accountStore.user)
    if (userKey) this.accountStore.setOldSelectedAccount(userKey, Number(account.id))

    try {
      const result = await this.bootstrapService.bootAccountOnly(Number(account.id), {
        refresh: true,
      })
      this.toolkit.hydrateAccountLevel(result)
    } catch (error) {
      return { status: 'failed', error: toApiError(error) }
    }

    // No preferred id: v3's account switch re-picks from the NEW account's freshly-hydrated list.
    const ws = this.toolkit.pickWorkspace()
    // v3 `:116` finalises even when the new account has no workspace at all (→ the onboarding
    // signal), so the no-workspace path is a finalize, not an early return.
    if (!ws) {
      // The new account has no workspace to switch INTO, but we are still leaving the old one:
      // cancel its in-flight requests and drop its workspace-scoped state before finalising.
      // Without this the onboarding surface this path leads to renders over the previous
      // account's hydrated sections, and its requests keep landing behind it.
      this.workspaceFlow.resetWorkspaceScope()
      this.workspaceFlow.finalize()
      // NO landing, even for an MCP-only account. This path IS the zero-workspace account, and the
      // guard orders onboarding ABOVE its MCP branch (`permissionAccess.ts`) — answering the MCP
      // shell here would land the user somewhere onboarding immediately pulls them back out of.
      // The onboarding target belongs to module 29 anyway, not to this flow.
      return { status: 'switched' }
    }

    const outcome = await this.workspaceFlow.switchTo(ws)
    if (outcome.status === 'failed') return outcome
    const landing = this.resolveLanding()
    // Spread rather than `landing: landing` — `exactOptionalPropertyTypes` forbids writing an
    // explicit `undefined` into an optional property, and an ABSENT key is what the web path means.
    return { status: 'switched', ...(landing ? { landing } : {}) }
  }

  /**
   * Where a completed switch should land, or `undefined` to leave the user exactly where they are
   * (ORP-21835 item 3).
   *
   * Resolved HERE because this is the first moment the answer exists: `is_mcp_only` rides
   * `account_detail`, which only the `bootAccountOnly` above hydrates. The switcher ROW does not
   * carry it — the backend added `is_mcp_only` to the accessible-accounts rows under ORP-21834, but
   * nothing on the frontend consumes that field yet, so the pre-selection answer is still unknown.
   *
   * This does NOT replace the router's MCP branch (`app/router/permissionAccess.ts`, ORP-21656),
   * which still owns the deep-link and refresh paths where no switch runs at all. It brings the
   * same verdict FORWARD to the switch, so an MCP-born account lands on its own shell instead of
   * leaving the user parked on a web screen until their next click bounces them off it.
   *
   * The exemptions must AGREE with that branch, or the switcher would yank a user the guard would
   * have left alone: sysadmin (support impersonating an MCP-only account, ORP-21656 AC 9) and the
   * admin-bypass persona (ORP-21835 item 4). Both are read from the same server-authoritative
   * `rbac` section the guard reads, `bootAccountOnly` having just refreshed it.
   *
   * `onlyDashboard` is deliberately NOT mirrored: the guard yields to it, but its target is
   * `resolveFirstDashboard()`, which lives in `app/` and cannot be computed from here. The
   * combination is unreachable in practice (the MCP product grants no shared-dashboard access) and
   * if it ever occurs the guard still settles it, in one further hop rather than a loop.
   *
   * Fail-safe WEB, exactly as `permissionSnapshot` reads the same bit: anything that is not
   * literally `true` — absent, a degraded `account_detail`, a stringy `'true'` — leaves the user in
   * place and lets the guard be the backstop. A missed landing costs one redirect later; a WRONG
   * one would throw a web user out of the product they do own.
   */
  private resolveLanding(): RouteLocationRaw | undefined {
    if (this.accountStore.accountDetail?.is_mcp_only !== true) return undefined
    if (this.accountStore.isSysAdmin || this.accountStore.canAccessAdminPanel) return undefined
    return MCP_LANDING
  }
}
